import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search, Edit, Trash2, Building2, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const initialFormData = {
  codigo: "",
  nombre: "",
  identificacion_fiscal: "",
  persona_contacto: "",
  telefono: "",
  telefono_otro: "",
  email: "",
  email_otro: "",
  direccion: "",
  pais: "",
  comentarios: "",
  logo_url: "",
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get("/suppliers");
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Error al cargar los proveedores");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditingId(supplier.id);
      setFormData({
        codigo: supplier.codigo || "",
        nombre: supplier.nombre || "",
        identificacion_fiscal: supplier.identificacion_fiscal || "",
        persona_contacto: supplier.persona_contacto || "",
        telefono: supplier.telefono || "",
        telefono_otro: supplier.telefono_otro || "",
        email: supplier.email || "",
        email_otro: supplier.email_otro || "",
        direccion: supplier.direccion || "",
        pais: supplier.pais || "",
        comentarios: supplier.comentarios || "",
        logo_url: supplier.logo_url || "",
      });
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  // Subir logo del proveedor
  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("tipo", "supplier_logo");

    try {
      const response = await api.post("/config/upload-logo", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const logoUrl = response.data.url;
      setFormData({ ...formData, logo_url: logoUrl });
      toast.success("Logo subido correctamente");
    } catch (error) {
      toast.error("Error al subir el logo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error("El nombre del proveedor es obligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/suppliers/${editingId}`, formData);
        toast.success("Proveedor actualizado correctamente");
      } else {
        await api.post("/suppliers", formData);
        toast.success("Proveedor creado correctamente");
      }
      fetchSuppliers();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el proveedor");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/suppliers/${deleteId}`);
      toast.success("Proveedor eliminado correctamente");
      setSuppliers(suppliers.filter((s) => s.id !== deleteId));
    } catch (error) {
      toast.error("Error al eliminar el proveedor");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(search.toLowerCase()) ||
      supplier.identificacion_fiscal?.toLowerCase().includes(search.toLowerCase()) ||
      supplier.codigo?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proveedores</h1>
          <p className="text-slate-500 mt-1">Gestión de proveedores y datos de contacto</p>
        </div>
        {hasRole(["admin", "proveedor"]) && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-supplier-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Proveedor
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, email, RIF o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-suppliers"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredSuppliers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay proveedores</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron proveedores con ese criterio" : "Comienza creando tu primer proveedor"}
          </p>
          {hasRole(["admin", "proveedor"]) && !search && (
            <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Crear Proveedor
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Código
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  RIF/CIF
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Contacto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Email
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  País
                </TableHead>
                {hasRole("admin") && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[100px]">
                    Acciones
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSuppliers.map((supplier) => (
                <TableRow key={supplier.id} className="hover:bg-slate-50" data-testid={`supplier-row-${supplier.id}`}>
                  <TableCell className="font-mono text-slate-500">{supplier.codigo || "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {supplier.logo_url ? (
                        <img src={supplier.logo_url} alt="" className="h-8 w-auto object-contain" />
                      ) : null}
                      <span className="font-medium">{supplier.nombre}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{supplier.identificacion_fiscal || "-"}</TableCell>
                  <TableCell>{supplier.persona_contacto || "-"}</TableCell>
                  <TableCell className="text-slate-500">{supplier.email || "-"}</TableCell>
                  <TableCell>{supplier.pais || "-"}</TableCell>
                  {hasRole("admin") && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(supplier)}
                          data-testid={`edit-supplier-${supplier.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(supplier.id)}
                          data-testid={`delete-supplier-${supplier.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Proveedor" : "Nuevo Proveedor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código (ID)</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Ej: P01"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre de la empresa"
                  required
                  data-testid="input-supplier-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Identificación Fiscal (RIF/CIF)</Label>
                <Input
                  value={formData.identificacion_fiscal}
                  onChange={(e) => setFormData({ ...formData, identificacion_fiscal: e.target.value })}
                  placeholder="Ej: J-50731006-0"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={formData.pais}
                  onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                  placeholder="Venezuela"
                />
              </div>
              <div className="space-y-2">
                <Label>Persona de Contacto</Label>
                <Input
                  value={formData.persona_contacto}
                  onChange={(e) => setFormData({ ...formData, persona_contacto: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono Principal</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Teléfono</Label>
                <Input
                  value={formData.telefono_otro}
                  onChange={(e) => setFormData({ ...formData, telefono_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email Principal</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Otro Email</Label>
                <Input
                  type="email"
                  value={formData.email_otro}
                  onChange={(e) => setFormData({ ...formData, email_otro: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Dirección completa"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Comentarios</Label>
                <Textarea
                  value={formData.comentarios}
                  onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                  placeholder="Notas adicionales..."
                  rows={3}
                />
              </div>
              
              {/* Logo del proveedor */}
              <div className="space-y-2 md:col-span-2">
                <Label>Logo del proveedor</Label>
                <div className="flex items-center gap-4">
                  {formData.logo_url ? (
                    <div className="relative">
                      <img 
                        src={formData.logo_url} 
                        alt="Logo" 
                        className="h-16 w-auto object-contain border rounded p-1"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, logo_url: "" })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-16 w-24 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400">
                      Sin logo
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadLogo}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {formData.logo_url ? "Cambiar" : "Subir logo"}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="save-supplier-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingId ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el proveedor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
